import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { DataTable } from "@/components/data";

import { FlagChip, FLAGS } from "../chips";

/** A titled box inside an Overview page (the mockup's card within the page card). */
export function Block({ title, aside, children }: { title: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-[8px] border border-ks-grey-3 p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-sm font-bold">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Two blocks a row, one under the other on a narrow screen. */
export function BlockGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>;
}

/** A short list drawn as a table: every row on screen, so the shell table never draws its pager. */
export function ListTable<Row>({
  columns,
  rows,
  caption,
  headerHidden,
  wrap,
  onRowClick,
  sort,
  onSort,
  rowClassName,
}: {
  columns: ColumnDef<Row, unknown>[];
  rows: Row[];
  caption: string;
  headerHidden?: boolean;
  wrap?: boolean;
  onRowClick?: (row: Row) => void;
  /** Passed through to the shell table: a column marked `sortable` gets a header button. */
  sort?: { id: string; direction: "asc" | "desc" };
  onSort?: (id: string) => void;
  rowClassName?: (row: Row) => string | undefined;
}) {
  return (
    <div className="overflow-x-auto">
      <DataTable<Row>
        columns={columns}
        rows={rows}
        page={{ pageIndex: 0, pageSize: Math.max(1, rows.length), rowCount: rows.length }}
        onPageChange={() => {}}
        density="catalogue"
        caption={caption}
        headerHidden={headerHidden}
        wrap={wrap}
        onRowClick={onRowClick}
        sort={sort}
        onSort={onSort}
        rowClassName={rowClassName}
      />
    </div>
  );
}

/** Said in a block that has no rating to show. */
export function NotScoredNote({ full = true }: { full?: boolean }) {
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-ks-grey-1">
      <FlagChip kind="not_scored" /> {full ? FLAGS.not_scored.tooltip : "No ratings yet."}
    </p>
  );
}
